import React, { createContext, useContext, useState, useCallback } from 'react';

const ChatPopupContext = createContext();

export const useChatPopup = () => {
  const context = useContext(ChatPopupContext);
  if (!context) {
    throw new Error('useChatPopup must be used within ChatPopupProvider');
  }
  return context;
};

/**
 * Manages state for multiple chat popup windows
 * Similar to Facebook's chat system
 */
export const ChatPopupProvider = ({ children }) => {
  const [openChats, setOpenChats] = useState([]); // Array of { conversationId, isMinimized }
  const MAX_OPEN_CHATS = 3; // Maximum number of open chat windows

  /**
   * Open a chat popup
   * @param {string} conversationId - ID of conversation to open
   */
  const openChat = useCallback((conversationId) => {
    setOpenChats(prev => {
      // Check if chat is already open
      const existingIndex = prev.findIndex(chat => chat.conversationId === conversationId);
      
      if (existingIndex !== -1) {
        // If minimized, restore it
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], isMinimized: false };
        return updated;
      }

      // If at max capacity, close the oldest chat
      let newChats = [...prev];
      if (newChats.length >= MAX_OPEN_CHATS) {
        newChats = newChats.slice(1); // Remove oldest (first) chat
      }

      // Add new chat
      return [...newChats, { conversationId, isMinimized: false }];
    });
  }, []);

  /**
   * Close a chat popup
   * @param {string} conversationId - ID of conversation to close
   */
  const closeChat = useCallback((conversationId) => {
    setOpenChats(prev => prev.filter(chat => chat.conversationId !== conversationId));
  }, []);

  /**
   * Minimize a chat popup
   * @param {string} conversationId - ID of conversation to minimize
   */
  const minimizeChat = useCallback((conversationId) => {
    setOpenChats(prev => 
      prev.map(chat => 
        chat.conversationId === conversationId 
          ? { ...chat, isMinimized: true }
          : chat
      )
    );
  }, []);

  /**
   * Restore a minimized chat
   * @param {string} conversationId - ID of conversation to restore
   */
  const restoreChat = useCallback((conversationId) => {
    setOpenChats(prev => 
      prev.map(chat => 
        chat.conversationId === conversationId 
          ? { ...chat, isMinimized: false }
          : chat
      )
    );
  }, []);

  /**
   * Toggle minimize state
   * @param {string} conversationId - ID of conversation to toggle
   */
  const toggleMinimize = useCallback((conversationId) => {
    setOpenChats(prev => 
      prev.map(chat => 
        chat.conversationId === conversationId 
          ? { ...chat, isMinimized: !chat.isMinimized }
          : chat
      )
    );
  }, []);

  /**
   * Close all chats
   */
  const closeAllChats = useCallback(() => {
    setOpenChats([]);
  }, []);

  /**
   * Check if a chat is open
   * @param {string} conversationId - ID of conversation to check
   * @returns {boolean}
   */
  const isChatOpen = useCallback((conversationId) => {
    return openChats.some(chat => chat.conversationId === conversationId);
  }, [openChats]);

  /**
   * Get minimized chats
   * @returns {Array} Array of minimized chat objects
   */
  const getMinimizedChats = useCallback(() => {
    return openChats.filter(chat => chat.isMinimized);
  }, [openChats]);

  /**
   * Get active (not minimized) chats
   * @returns {Array} Array of active chat objects
   */
  const getActiveChats = useCallback(() => {
    return openChats.filter(chat => !chat.isMinimized);
  }, [openChats]);

  const value = {
    openChats,
    openChat,
    closeChat,
    minimizeChat,
    restoreChat,
    toggleMinimize,
    closeAllChats,
    isChatOpen,
    getMinimizedChats,
    getActiveChats,
    MAX_OPEN_CHATS,
    isMinimized: useCallback((conversationId) => {
      return openChats.some(chat => chat.conversationId === conversationId && chat.isMinimized);
    }, [openChats]),
  };

  return (
    <ChatPopupContext.Provider value={value}>
      {children}
    </ChatPopupContext.Provider>
  );
};

export default ChatPopupContext;
